import { useEffect, useRef, useState } from "react";
import type { Order, Sendung } from "../../lib/types.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import type { AktiveSeite } from "./Navigation.tsx";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import { zeitstrahlSendungenLaden } from "./zeitstrahlSendungen.ts";

/**
 * Eigener Sendungsbestand der Zeitstrahl-Liste, unabhaengig vom offenen Speditions-Drawer.
 * Ein fehlender Schluessel in `{}` bedeutet wie `null` den Board-Rueckfall.
 * Beide Ladefehler gehen seit der Fehleranzeige (Paket 1) in den Fehlerstapel; einen Toast gibt es hier nicht.
 */
export function useZeitstrahlSendungen(
  seite: AktiveSeite,
  bestellungen: Order[],
  definitionen: StatusDefinitionen,
) {
  const [zeitstrahlSendungen, setZeitstrahlSendungen] = useState<Record<string, Sendung | null>>({});
  // Die aktuelle Seite fuer `zeitstrahlListeNeuLaden`: der Abruf dauert, und die Funktion wird von Aktionen gehalten, die
  // vor dem Seitenwechsel gestartet wurden - eine Closure ueber `seite` sahe die Seite vom Start des Abrufs.
  const seiteRef = useRef(seite);
  seiteRef.current = seite;

  /**
   * Nach Sammelsendung-Aufloesen/Herausnehmen oder Verknuepfen/Loesen eigenstaendig nachladen.
   * Kein Promise an die Aktionskette zurueckgeben: ein Abruffehler darf ihren Erfolg nicht
   * zuruecknehmen. Frueher bewusst nur Log, weil ein Toast die Aktionsmeldung ueberschrieben haette
   * (Entscheidung "Sammelroute nachbessern", 25.08.2026); der Fehlerstapel ueberschreibt nichts. Die
   * Aktion IST gelungen (R4): der Eintrag sagt nur, dass die Liste veraltet sein kann (ohne Wiederholungsrat der
   * Ursache, R-Z2).
   *
   * Der Eintrag kommt NUR, wenn die Seite beim Scheitern der Zeitstrahl ist (R-Z5): die Funktion laeuft nach
   * Sammelsendungs-Aktionen auf jeder Seite, die Liste ist aber nur dort sichtbar und laedt beim Oeffnen ohnehin neu
   * (Effekt unten). Auf jeder anderen Seite bleibt es bei `console.error`.
   */
  function zeitstrahlListeNeuLaden(): void {
    zeitstrahlSendungenLaden(bestellungen, definitionen)
      .then(setZeitstrahlSendungen)
      .catch((fehler: unknown) => {
        console.error("Sendungen zur Zeitstrahl-Liste konnten nicht neu geladen werden:", fehler);
        if (seiteRef.current === "timeline") {
          meldeNachladeFehler(
            fehler,
            "Die Sendungen zur Zeitstrahl-Liste konnten nicht neu geladen werden. Bitte die Seite neu laden.",
          );
        }
      });
  }

  // Die Seite des vorigen Effektlaufs - auch dann fortgeschrieben, wenn der Lauf unten abbricht
  // (Seite ist nicht der Zeitstrahl), damit "Seite hat gewechselt" richtig erkannt wird.
  const vorherigeSeite = useRef<AktiveSeite | null>(null);

  // Nur auf der sichtbaren Zeitstrahl-Seite laden, nicht bei jedem App-Start (22.08.2026).
  // Seitenwechsel, neuer Bestellungs-/Definitionenbestand oder Unmount verwerfen den alten Abruf.
  //
  // "Aktiv" heisst: der Mensch hat etwas getan. Oeffnet er den Zeitstrahl (`seite` wechselt), ist der
  // Abruf seine Handlung. Wechselt bei gleicher Seite nur der Bestand (SSE-Nachladen ersetzt das
  // `bestellungen`-Array), laedt die Anwendung von selbst nach: dann geht die Anfrage als
  // Hintergrundanfrage hinaus (`AnfrageOptionen.hintergrund`) und zaehlt nicht als Aktivitaet.
  useEffect(() => {
    const seiteGewechselt = vorherigeSeite.current !== seite;
    vorherigeSeite.current = seite;
    if (seite !== "timeline") return;
    let verworfen = false;
    // Das dritte Argument nur bei `true`: der Aufruf der Handlung bleibt der mit zwei Argumenten.
    (seiteGewechselt
      ? zeitstrahlSendungenLaden(bestellungen, definitionen)
      : zeitstrahlSendungenLaden(bestellungen, definitionen, true)
    )
      .then((geladen) => {
        if (!verworfen) setZeitstrahlSendungen(geladen);
      })
      .catch((fehler: unknown) => {
        if (!verworfen) {
          // Der Seitenabruf braucht eine sichtbare Fehlermeldung: der Board-Rueckfall waere sonst vom
          // normalen Zustand "noch nicht geladen" ununterscheidbar.
          console.error("Sendungen zur Zeitstrahl-Liste konnten nicht geladen werden:", fehler);
          meldeFehler(fehler, "Die Sendungen zur Zeitstrahl-Liste konnten nicht geladen werden.");
        }
      });
    return () => {
      verworfen = true;
    };
  }, [seite, bestellungen, definitionen]);

  return { zeitstrahlSendungen, zeitstrahlListeNeuLaden };
}
